import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

import { IncreasePriceDown, IncreasePriceUp } from '@lt/components/icons-react';
import { ArrowRightLong } from '@lt/components/icons-react/userflow';
import { CurrencyFormat } from '@lt/components';
import { TEST_ID_PRICE_CHEKER_WARNING } from './constants';
import { getPriceNoticeText } from './utils';
import type { PriceDirection } from '../../types';

type Props = {
  priceChanged: boolean;
  changeDirection?: PriceDirection;
  isPriceChangedSignificantly: boolean;
  oldPrice?: number;
  currentPrice?: number;
};

const PriceChangeWarning = (props: Props) => {
  const {
    priceChanged,
    changeDirection,
    isPriceChangedSignificantly,
    oldPrice,
    currentPrice,
  } = props;

  const { title, description } = useMemo(
    () => getPriceNoticeText(changeDirection, isPriceChangedSignificantly),
    [changeDirection, isPriceChangedSignificantly],
  );

  if (!priceChanged || !changeDirection) return null;

  return (
    <Wrapper
      $priceIncreased={changeDirection === 'up'}
      data-testid={TEST_ID_PRICE_CHEKER_WARNING}
    >
      {changeDirection === 'up' ? <PriceIncreaseIcon /> : <PriceDecreaseIcon />}
      <Text>
        <PriceChangeDescription>
          {title} {description}
        </PriceChangeDescription>
        <PriceComparer>
          <OldPrice value={oldPrice} />
          <ArrowRightIcon />
          <NewPrice value={currentPrice} />
        </PriceComparer>
      </Text>
    </Wrapper>
  );
};

const fadeIn = keyframes`
  0% {
    transform: translateY(-25%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ArrowRightIcon = styled(ArrowRightLong)`
  width: 30px;
  stroke: #4d4d4d;
`;

const PriceIncreaseIcon = styled(IncreasePriceUp)`
  width: 16px;
  height: 18px;
  flex-shrink: 0;
`;

const PriceDecreaseIcon = styled(IncreasePriceDown)`
  width: 16px;
  height: 18px;
  flex-shrink: 0;
`;

const Text = styled.div`
  margin-left: 4px;

  > * + * {
    margin-top: 2px;
  }
`;

const PriceChangeDescription = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const Wrapper = styled.div<{ $priceIncreased: boolean }>`
  background: ${(props) => (props.$priceIncreased ? '#f9eaec' : '#F4F9DF')};
  padding: 4px;
  border-radius: 4px;
  display: flex;

  animation: ${fadeIn} 0.25s ease-in-out;
  height: 100%;
`;

const PriceComparer = styled.div`
  display: flex;
  align-items: baseline;

  > * + * {
    margin-left: 4px;
  }
`;
const OldPrice = styled(CurrencyFormat)`
  text-decoration: line-through;
  color: #4d4d4d;
`;
const NewPrice = styled(CurrencyFormat)`
  font-weight: 700;
  color: #17181a;
`;

export default PriceChangeWarning;
